import { FC } from 'react';

import { CloseButton } from 'components';
import { store } from 'store';

import * as S from './Whitepaper.styles';

export const Whitepaper: FC = () => (
  <S.Overlay>
    <CloseButton onClick={store.commonStore.resetMode} />
    <S.Content>
      {/* TITLE PAGE */}
      <S.TitlePage>
        <h1>WOWMAX — Verifiable Data for AI Agents</h1>
        <h2>Intelligence Infrastructure for the Global On‑Chain Economy</h2>

        <S.Version>Whitepaper v1.5</S.Version>

        <S.Meta>
          <strong>Author:</strong> Almir Salimov, MBA (INSEAD)
          <br />
          <strong>Affiliation:</strong> WOWMAX Limited, Hong Kong
          <br />
          <strong>Date:</strong> February 2026
        </S.Meta>

        <S.Contact>
          <strong>Contact:</strong>
          <br />
          GitHub:{' '}
          <a href="https://github.com/AlmirSalimov777" target="_blank" rel="noreferrer">
            AlmirSalimov777
          </a>
          <br />
          Telegram:{' '}
          <a href="https://t.me/Almir" target="_blank" rel="noreferrer">
            @Almir
          </a>
        </S.Contact>

        <S.Contact>
          <strong>Awards &amp; Recognitions:</strong>
          <S.AwardsList>
            <li>
              Finalist — DevConnect 2025 Crecimiento startup competition (Top 12 out of 212
              projects)
            </li>
            <li>
              2nd Place — Cointelegraph 2025 startup competition during DevConnect in Argentina
            </li>
            <li>Global Winner 2024 — Soneium Spark Award (among 1,700 participants)</li>
          </S.AwardsList>
        </S.Contact>

        <S.Contact>
          <strong>Early Backers:</strong>
          <br />
          Outlier Ventures, Burrito Wallet (founded by Bithumb), Hilbert Group (Nasdaq listed),
          Waterdrip Capital, Cointelegraph, Loyal VC.
        </S.Contact>

        <S.Contact>
          <strong>Advisors:</strong>
          <br />
          <a href="https://www.linkedin.com/in/bhavdeep-dhingra" target="_blank" rel="noreferrer">
            Bhavdeep Dhingra
          </a>
        </S.Contact>

        <S.Abstract>
          This document describes the architecture, economic model, and product roadmap for the
          WOWMAX Verifiable Data for AI Agents — a modular infrastructure that transforms fragmented
          on‑chain data into a single AI‑queryable, verifiable, and economically incentivized
          network.
        </S.Abstract>
      </S.TitlePage>

      {/* EXECUTIVE SUMMARY */}
      <h2>Executive Summary</h2>

      <p>
        The global financial system is migrating to blockchain. Real estate, commodities,
        securities, currencies — trillions of dollars in assets are being tokenized across hundreds
        of chains, thousands of protocols, and millions of wallets.
      </p>

      <p>
        But the data layer hasn't kept up. The on-chain economy already produces more financial data
        than any human or institution can process — and it's accelerating. The result: the most
        data-rich financial system ever created, with the worst data infrastructure. Today's tools
        output data for humans staring at screens. The next generation of financial interfaces —
        voice agents, autonomous trading systems, AI advisors — need something fundamentally
        different.
      </p>

      <p>
        <strong>
          WOWMAX is building the AI‑native, verifiable data infrastructure that makes the entire
          on‑chain economy queryable, verifiable, and actionable — in natural language.
        </strong>
      </p>

      <S.QvaSection>
        <S.QvaText>
          <ul>
            <li>
              <strong>Queryable</strong> — any question about any on-chain asset, market, or
              protocol can be answered through a single natural language request, without requiring
              specialized tools, APIs, or technical expertise.
            </li>
            <li>
              <strong>Verifiable</strong> — every answer traces back to auditable sources with full
              provenance: who provided the data, which methodology was applied, when it was
              observed. No black boxes, no hallucinations.
            </li>
            <li>
              <strong>Actionable</strong> — answers are not just informational. They are structured,
              machine-readable, and directly connected to execution — from "this pool has the safest
              yield" to one-click liquidity provision through WOWMAX smart contracts.
            </li>
          </ul>
        </S.QvaText>
        <S.QvaFrog>
          <pre>
            {`                                    ..---._
                      _..-\`\`\`-._ ⋰\`\`        '-.
                   .'-.--.    '\`\`\`\`\`\`\`\`-..     '.
             _.-''''--.. _.-'_'''--., \`\`\`-..      \\
            /           \\  /         \`⁻ -..        \\
           |    (•)      ||   (•)        ⋰         \\
           \\          _/ ||            ⋰ ⋰           \\
            \\____---\`\` _/ \\\\__‿__...-  ⋰              \\
              \\_______/    '._.._....-'  ..            \\
             /       /          .._....-'    __ \\       |
            /\\_                        __..-'  \\ \\      |
            \\__\`\`--... .. .. .. \`\` \`\`' .--     -'       |
               /'''--...--..----\`\`\`\`     -'  -'         /
             /  /                   _.-   .-'          /
             | ''--...______...---\`\`  _.-'          .-'
              \\ \\_                ..-'          _.-'
               \`\`-\`\`--... __..--''          _.-''
               /\\    \`\`--...________....---''
              /  \\
            .-=================-.
         .-'                     '-
        /           Q.V.A          \\
       |                            |
       |            FEELS           |
        \\                          /
         '.         GOOD         .'
           '------------- -----''`}
          </pre>
        </S.QvaFrog>
      </S.QvaSection>

      <p>
        We are not starting from zero. WOWMAX already operates a cross‑chain DEX aggregator that has
        processed <strong>$2B in trading volume</strong>, served{' '}
        <strong>336,000+ unique traders</strong>, and executed <strong>2,000,000+ swaps</strong>{' '}
        across multiple blockchain networks. This creates a uniquely strong foundation: a live,
        battle‑tested pipeline into real‑time multi‑chain trading data and the team that built it.
      </p>

      <p>
        The WOWMAX AI Data Layer extends this foundation into a programmable, token‑incentivized
        query economy where participants contribute data, publish structured Query Modules, provide
        execution capacity, and earn from real usage.
      </p>

      <p>
        <strong>Our thesis:</strong> what takes 30 minutes and five tools today should take{' '}
        <strong>one sentence and three seconds</strong>.
      </p>

      <hr />

      {/* SECTION 1 */}
      <h2>1. Why Now</h2>

      <p>Three converging forces make a verifiable query layer inevitable:</p>

      <p>
        <strong>1. The fragmentation is already unmanageable — and it's getting worse.</strong>
        <br />
        On-chain and hybrid assets now include tokenized real estate, commodities, securities,
        stablecoins (
        <a href="https://defillama.com/stablecoins" target="_blank" rel="noreferrer">
          $300B+ market
        </a>
        ), 10,000+ actively traded tokens, derivatives, and governance tokens controlling{' '}
        <a href="https://defillama.com/" target="_blank" rel="noreferrer">
          $100B in DeFi vaults
        </a>
        . These assets trade across hundreds of DEXs, CEXs, launchpads, prediction markets, lending
        protocols, bridges, and yield platforms — on hundreds of blockchain networks. Each uses
        different formats, schemas, latency profiles, and reliability guarantees. Nobody has a
        complete picture. Nobody.
      </p>

      <p>
        <strong>
          2. AI is becoming the default interface — but AI without verified data is a liability, not
          an asset.
        </strong>
        <br />
        Tool‑calling models (GPT 5.2 Pro, Opus 4.6, open-source LLMs) can now reliably generate
        structured requests. This is a revolution. But without verified tools and deterministic
        execution, AI doesn't solve the data problem — it amplifies it. A confident-sounding wrong
        answer is more dangerous than no answer at all. AI needs structured, permissioned,
        verifiable tools — or it becomes the most sophisticated misinformation engine finance has
        ever seen.
      </p>

      <p>
        <strong>3. Auditability and compliance demands are rising.</strong>
        <br />
        MiCA in Europe, evolving SEC frameworks in the US, and progressive regulation in Asia create
        a landscape where institutions and serious operators need reproducible answers, provenance,
        and verifiable methodology — not "best effort" analytics. BlackRock, Franklin Templeton, and
        major institutions are tokenizing funds on-chain. Industry projections for tokenized assets
        by 2030 range from{' '}
        <a
          href="https://www.mckinsey.com/industries/financial-services/our-insights/tokenization-a-digital-asset-deja-vu"
          target="_blank"
          rel="noreferrer"
        >
          $2 trillion (McKinsey)
        </a>{' '}
        to{' '}
        <a
          href="https://www.coindesk.com/opinion/2025/02/07/rwa-tokenization-is-going-to-trillions-much-faster-than-you-think"
          target="_blank"
          rel="noreferrer"
        >
          $30 trillion (Security Token Market)
        </a>
        , with{' '}
        <a
          href="https://www.theblock.co/post/386588/tokenization-outlook-ark-invest"
          target="_blank"
          rel="noreferrer"
        >
          ARK Invest's Big Ideas 2026
        </a>{' '}
        projecting $11 trillion and{' '}
        <a
          href="https://www.assettokenization.com/resources/asset-tokenization-forecasts-range-from-2t-to-30t-by-2030"
          target="_blank"
          rel="noreferrer"
        >
          BCG/Ripple (2025)
        </a>{' '}
        estimating $9.4 trillion. Each new asset class creates new data complexity that demands
        institutional-grade infrastructure.
      </p>

      <p>
        The window to build the data layer for this economy is now — before the ecosystem fragments
        further and data silos become entrenched.
      </p>

      <hr />

      {/* SECTION 2 */}
      <h2>2. The Problem: The Trust / Interpretation Gap</h2>

      <p>
        The bottleneck is no longer data access — it is <strong>trustworthy interpretation</strong>.
      </p>

      <h3>2.1 "Truth is not a number"</h3>

      <p>
        A single "simple" decision — <em>"Should I provide liquidity here?"</em> — requires
        aggregating:
      </p>

      <ul>
        <li>Historical volume, impermanent loss and ROI data</li>
        <li>Protocol risk and audit status</li>
        <li>Smart contract upgradeability / admin risk</li>
        <li>Wallet concentration and unlock schedules for a reward token</li>
        <li>DEX liquidity depth for tokens involved</li>
        <li>CEX spread/volume validity (wash trading, spoofing)</li>
        <li>Cross‑chain wrapped exposure and bridge dependencies</li>
        <li>Social/external signals (optional, scored)</li>
      </ul>

      <p>
        This requires 5+ tools, 3+ data sources, and 30+ minutes of manual work — for a single
        question. For AI agents and autonomous systems, it's worse — there is no reliable
        programmatic path to a verified answer. The tools don't exist. Not "aren't good enough."
        Don't exist.
      </p>

      <h3>2.2 Why current solutions fail for the AI era</h3>

      <p>
        <strong>The Graph</strong> indexes blockchain events but provides no intelligence — raw
        data, no context, no tool-callable interface for AI agents.
      </p>

      <p>
        <strong>Dune Analytics</strong> offers SQL dashboards but requires technical expertise and
        provides no real-time programmatic access for AI systems.
      </p>

      <p>
        <strong>Chainlink</strong> delivers price feeds but cannot answer compound questions like
        "What is the real liquidity risk of this token across all chains?"
      </p>

      <p>
        <strong>DefiLlama</strong> aggregates TVL metrics but cannot perform cross-source
        verification or serve as a structured tool layer for AI agents.
      </p>

      <p>
        These platforms solve important parts of the stack. But none of them were designed for the
        AI era. They output data for humans staring at screens. The next generation of financial
        interfaces — voice agents, autonomous trading systems, AI advisors — need something
        fundamentally different: <strong>structured, verified, tool-callable data.</strong>
      </p>

      <hr />

      {/* SECTION 3 */}
      <h2>3. What "Verifiable" Means in WOWMAX</h2>

      <p>
        "Verifiable" is not a slogan — it is a system property. WOWMAX defines it in three practical
        layers:
      </p>

      <h3>3.1 Provenance (Where did this come from?)</h3>
      <p>Every response includes:</p>
      <ul>
        <li>Source identifiers (providers/adapters used)</li>
        <li>Module versions (which Query Module implementation)</li>
        <li>Timestamps (observed_at, computed_at)</li>
        <li>Parameters used (inputs + constraints)</li>
      </ul>

      <h3>3.2 Determinism (Can it be reproduced?)</h3>
      <p>
        Given a module version, the same inputs/parameters, and declared sources/execution settings
        — the query reproduces the same output (within defined tolerances for time‑dependent feeds).
      </p>

      <h3>3.3 Auditability (Can someone independently check it?)</h3>
      <p>The system exposes:</p>
      <ul>
        <li>A query receipt (intent → module + parameters)</li>
        <li>Structured output schema</li>
        <li>Evidence/provenance metadata</li>
        <li>Optional replay paths for independent verification</li>
      </ul>

      <p>
        <strong>Progressive strengthening roadmap:</strong> quorum execution, disputes/challenges,
        SLA proofs, reputation scoring, cryptographic attestations for selected lanes.
      </p>

      <hr />

      {/* SECTION 4 */}
      <h2>4. The Solution: WOWMAX AI Data Layer</h2>

      <p>
        WOWMAX AI Data Layer is modular infrastructure that transforms fragmented on‑chain data into
        a single <strong>AI‑queryable, verifiable, economically incentivized network</strong>.
      </p>

      <p>Core design principles:</p>

      <p>
        <strong>Verified, not hallucinated.</strong> AI never invents data. It orchestrates
        registered modules that return evidence and provenance. Every number traces to a source.
        Every source has an owner. Every owner has skin in the game.
      </p>

      <p>
        <strong>Structured, not raw.</strong> Raw blockchain data is useless to an AI agent. Outputs
        are normalized through typed Query Modules with validation and schemas — built for machines,
        agents, and humans equally. Data becomes intelligence only when it has structure.
      </p>

      <p>
        <strong>Incentivized, not extracted.</strong> Most data platforms extract value from
        contributors. WOWMAX distributes it. Each query creates economic value that flows to the
        participants who provide data, verify it, build modules, and run infrastructure. The economy
        is aligned with real demand, not speculation.
      </p>

      <hr />

      {/* SECTION 5 */}
      <h2>5. Threat Model</h2>

      <p>A verifiable data layer for finance must assume adversarial conditions:</p>

      <h3>Market manipulation &amp; integrity</h3>
      <ul>
        <li>Token wash trading on CEXs and DEXs</li>
        <li>
          Sybil attacks on trust signals: thousands of wallets and social accounts fabricated to
          simulate genuine token holders, community activity, and organic demand
        </li>
        <li>MEV distortions and venue anomalies</li>
      </ul>

      <h3>Data provider / pipeline risks</h3>
      <ul>
        <li>
          Outdated data served as current — a price feed showing yesterday's price as "live,"
          leading to trades based on wrong numbers
        </li>
        <li>
          APIs changing without warning — a data source quietly changes its format, and downstream
          systems break or return garbage without anyone noticing
        </li>
        <li>
          Partial outages — a provider goes down for one chain but stays up for others, creating an
          invisible blind spot in cross-chain analysis
        </li>
        <li>
          Black-box scoring — platforms publishing "risk scores" or "trust ratings" with no
          explanation of how they're calculated, making it impossible to verify or challenge the
          methodology
        </li>
      </ul>

      <h3>On‑chain risks</h3>
      <ul>
        <li>Upgradeable proxies and admin keys</li>
        <li>Bridge dependencies and wrapped asset double counting</li>
        <li>Reorg / indexer inconsistencies</li>
      </ul>

      <p>
        WOWMAX addresses these via: module‑level validation, provenance capture, multi‑source
        consistency checks, anomaly detection, and incentive‑aligned quality markets.
      </p>

      <hr />

      {/* SECTION 6 */}
      <h2>6. Architecture</h2>

      <h3>6.1 The Stack</h3>

      <pre>
        {`┌────────────────────────────────────────────────┐
│  Layer 6: Interfaces                           │
│  AI Chat · Voice Agents · APIs · Widgets       │
├────────────────────────────────────────────────┤
│  Layer 5: AI Orchestrator                      │
│  Intent → Module Selection → Validation →      │
│  Tool Calling → Response Assembly              │
├────────────────────────────────────────────────┤
│  Layer 4: Execution Infrastructure             │
│  Distributed Compute · Load Balancing ·        │
│  Metering · Reliability Targets                │
├────────────────────────────────────────────────┤
│  Layer 3: Query Modules                        │
│  Typed Interfaces · Validation Rules ·         │
│  Aggregation Logic · Output Schemas            │
├────────────────────────────────────────────────┤
│  Layer 2: Data Normalization                   │
│  Indexing · Standardization · Deduplication ·  │
│  Cross-Chain Mapping · Latency Policies        │
├────────────────────────────────────────────────┤
│  Layer 1: Data Sources                         │
│  Blockchains · CEX APIs · RWA Registries ·     │
│  Oracles · Social Feeds · Financial Data       │
└────────────────────────────────────────────────┘`}
      </pre>

      <h3>6.2 Query Modules — The Core Abstraction</h3>

      <p>
        A common misconception: "just connect an LLM to a database and let it answer questions." In
        reality, this doesn't work for financial data. An LLM cannot write a correct SQL query that
        joins 12 tables, applies time-weighted PnL calculations, filters for statistical
        significance, and performs regression analysis across millions of trading records — and even
        if it could, the raw output would be unstructured, unverified, and impossible to audit.
      </p>

      <p>
        Query Modules solve this. They are the translation layer between a simple human question and
        the complex analytical pipeline required to answer it correctly. A Query Module takes
        natural language intent, converts it into optimized database queries, applies
        domain-specific statistical and economic analysis, validates the output against known
        constraints, and returns a structured, auditable result with full provenance.
      </p>

      <p>A Query Module defines:</p>
      <ul>
        <li>
          <strong>Intent:</strong> what question it answers
        </li>
        <li>
          <strong>Input schema:</strong> parameters with validation rules
        </li>
        <li>
          <strong>Sources:</strong> which verified feeds/adapters it consumes
        </li>
        <li>
          <strong>Methodology:</strong> normalization, aggregation, and analytical logic
          (transparent, auditable)
        </li>
        <li>
          <strong>Output schema:</strong> exact response structure
        </li>
        <li>
          <strong>Provenance:</strong> sources/versions/timestamps included in every response
        </li>
      </ul>

      <p>
        <strong>Example: Copy-Trading Intelligence Module</strong>
      </p>

      <p>
        The naive approach to copy-trading is to find wallets with the highest returns and copy
        them. This is a trap. The top-performing wallets are typically aggressive trading bots —
        they front-run, snipe launches, and dump tokens within seconds. By the time you copy their
        trade, they've already exited. You buy their exit liquidity.
      </p>

      <p>
        WOWMAX solves this differently. Our Copy-Trading Intelligence Module doesn't rank wallets by
        their own profit. It ranks wallets by the ROI their followers actually achieve.
      </p>

      <pre>
        {`Module: copy_trading_intelligence
Intent: "Find wallets worth copying — based on follower outcomes, not wallet profit"

How it works:
  1. Analyze 200K+ wallets across BNB Chain
  2. For each wallet, identify addresses that consistently buy
     the same tokens shortly after
  3. Calculate the actual ROI these followers achieved —
     accounting for slippage, timing delay, and exit liquidity
  4. Rank wallets by follower ROI, not by the wallet's own returns
  5. Filter out bots, snipers, and wallets with patterns indicating
     they profit from followers buying behind them

Result:
  - Curated list of wallets whose trading patterns are genuinely
    replicable by real users
  - Risk profile for each wallet (conservative / moderate / aggressive)
  - Historical follower ROI with confidence intervals
  - Full provenance: data sources, analysis methodology, timestamps`}
      </pre>

      <p>
        This is not theoretical — WOWMAX already runs this analysis on{' '}
        <strong>200K+ wallets</strong> for its copy-trading platform. The Query Module formalizes
        what we already do into a composable, monetizable, AI-callable interface.
      </p>

      <h3>6.3 AI Orchestrator — Tool-Calling by Design</h3>

      <p>
        <strong>How a query flows:</strong>
      </p>

      <ol>
        <li>
          User asks: <em>"What's the safest stablecoin yield above 5% on Ethereum right now?"</em>
        </li>
        <li>Orchestrator detects intent and decomposes into sub‑queries</li>
        <li>
          Selects modules: <code>yield_scanner</code> + <code>token_risk_profile</code> +{' '}
          <code>protocol_audit_status</code>
        </li>
        <li>Validates parameters and routes to execution infrastructure</li>
        <li>Modules execute against verified data sources</li>
        <li>Orchestrator assembles a response with structured outputs and provenance</li>
      </ol>

      <p>
        <strong>Critical constraint:</strong> AI never fetches arbitrary data. It can{' '}
        <strong>only</strong> interact through registered, validated Query Modules. This eliminates
        hallucination at the data level — the most dangerous failure mode for financial AI.
      </p>

      <hr />

      {/* SECTION 7 */}
      <h2>7. Canonical Queries — What WOWMAX Delivers First</h2>

      <p>These are "standard questions" that become deterministic, verifiable calls:</p>

      <h3>7.1 Real Liquidity &amp; Market Quality</h3>
      <p>
        <strong>Intent:</strong> "How real is liquidity for TOKEN X?"
        <br />
        <strong>Outputs:</strong> DEX/CEX liquidity by venue, stability metrics, concentration
        analysis, manipulation flags, confidence score + full provenance.
      </p>

      <h3>7.2 Safest Yield Above X%</h3>
      <p>
        <strong>Intent:</strong> "Safest stablecoin yield {'>'} 5% on Arbitrum now."
        <br />
        <strong>Outputs:</strong> Candidate opportunities ranked by risk-adjusted return, protocol
        risk assessment, asset risk, evidence, provenance.
      </p>

      <h3>7.3 Contract / Protocol Risk Snapshot</h3>
      <p>
        <strong>Intent:</strong> "Is this contract safe to integrate?"
        <br />
        <strong>Outputs:</strong> Upgradeability/admin risk, dependency map, audit registry status,
        known vulnerabilities, evidence + provenance.
      </p>

      <h3>7.4 Cross‑Chain Exposure Map</h3>
      <p>
        <strong>Intent:</strong> "What's my true exposure across chains?"
        <br />
        <strong>Outputs:</strong> Asset identity graph, wrapped/bridged dependencies, double‑count
        guards, exposure breakdown + provenance.
      </p>

      <h3>7.5 Trader Intelligence</h3>
      <p>
        <strong>Intent:</strong> "Show me the most profitable BNB Chain traders this month."
        <br />
        <strong>Outputs:</strong> Ranked trader profiles, PnL, win rate, trading patterns,
        copy-trade scores, risk profiles + provenance.
      </p>

      <hr />

      {/* SECTION 8 */}
      <h2>8. Why This Is Hard to Replicate</h2>

      <p>Building this requires three capabilities that almost never exist in the same team:</p>

      <p>
        <strong>1. Live multi‑chain trading infrastructure.</strong>
        <br />
        Not dashboards. Not APIs. Execution-grade connectivity and routing across DEXs and chains —
        the kind that moves real money. WOWMAX has processed $2B in actual trading volume. This
        gives us something no pure data company can buy: ground truth about how markets actually
        work.
      </p>

      <p>
        <strong>2. Deep DeFi domain expertise.</strong>
        <br />
        Not "we read the docs." Understanding how liquidity behaves when it's being attacked: MEV
        extraction, wash trading, bridge exploits, impermanent loss at scale, token emission
        spirals. Our team combines Wall Street quantitative trading with years of building on-chain
        systems in adversarial conditions.
      </p>

      <p>
        <strong>3. AI engineering for financial tool‑calling.</strong>
        <br />
        Not a chatbot with a blockchain API key. Validated schemas, deterministic execution paths,
        provenance metadata, safety constraints, sub-second latency. Sophie, our voice AI assistant,
        already demonstrates this architecture in production — talking to real users about real
        markets.
      </p>

      <p>
        No pure data company has the trading infrastructure. No pure trading company has the AI
        layer. No pure AI company has the DeFi expertise.{' '}
        <strong>The moat is the intersection.</strong>
      </p>

      <hr />

      {/* SECTION 9 */}
      <h2>9. The Data Economy</h2>

      <h3>9.1 Participant Roles</h3>

      <p>
        <strong>Data Providers</strong>
        <br />
        Entities that contribute verified data feeds: blockchain indexers, market data aggregators,
        risk scoring services, RWA verification oracles, social analytics platforms. They register
        data sources with quality guarantees (freshness, accuracy, uptime) and earn tokens
        proportional to the queries that consume their data.
      </p>

      <p>
        <strong>Module Developers</strong>
        <br />
        Developers who publish Query Modules — the structured interfaces that make raw data
        AI-consumable. They define schemas, validation logic, methodology, and aggregation rules.
        They earn a share of every query that uses their module. This creates a{' '}
        <strong>developer marketplace for financial intelligence</strong> — think npm for on-chain
        data queries.
      </p>

      <p>
        <strong>Infrastructure Operators</strong>
        <br />
        Participants who provide computational capacity to execute queries with reliability targets.
        They run execution nodes, meet SLA requirements, and earn based on delivered throughput and
        measured reliability.
      </p>

      <p>
        <strong>Data Verifiers</strong>
        <br />
        Human participants who stake $WOWMAX tokens and take responsibility for the accuracy of data
        flowing through the network. They validate sources, flag anomalies, cross-check module
        outputs, and audit provenance chains. They earn a share of query fees for data they have
        verified — and risk slashing if that data proves false. (See Section 10 for full details.)
      </p>

      <h3>9.2 Token Design ($WOWMAX)</h3>

      <p>
        The network uses a single utility token: <strong>$WOWMAX</strong>.
      </p>

      <p>
        <strong>Utility:</strong>
      </p>
      <ul>
        <li>Pay for query execution</li>
        <li>
          Reward contributors (data providers, module developers, data verifiers, infrastructure
          operators)
        </li>
        <li>Stake/collateral for data verification and infrastructure reliability</li>
        <li>Governance over protocol parameters (future)</li>
      </ul>

      <p>
        <strong>Fee flow per query (illustrative):</strong>
      </p>

      <pre className="fee-tree">
        {`User pays X tokens
  ├── Data Providers:     30%
  ├── Module Developers:  10%
  ├── Data Verifiers:     20%
  ├── Infra Operators:    20%
  └── Protocol Treasury:  20%`}
      </pre>

      <p>
        <strong>Sustainability principles:</strong>
      </p>
      <ul>
        <li>Rewards are primarily usage‑funded (query fees), not infinite emissions</li>
        <li>Parameters are adjustable by governance as the network matures</li>
        <li>Treasury mechanisms (burn/buyback) are policy‑driven, not guarantees</li>
        <li>No mining, no uncapped inflation</li>
      </ul>

      <hr />

      {/* SECTION 10 */}
      <h2>10. The Human Verification Network: AI Creates Jobs</h2>

      <h3>10.1 The Narrative Nobody Is Telling</h3>

      <p>
        Everyone is talking about AI replacing humans. Nobody is talking about the new jobs AI can't
        do without.
      </p>

      <p>
        Here's the uncomfortable truth: <strong>AI is only as good as the data it consumes.</strong>{' '}
        And in finance, bad data doesn't just produce bad answers — it produces confident bad
        answers that move money in the wrong direction. A language model will tell you a token has
        $50M market cap and 20,000 holders while in reality the token has $10,000 of DEX liquidity
        and 19,999 airdropped wallets masquerading as organic community. It will cite an audit that
        doesn't exist. It will synthesize contradictory sources into a single number and present it
        as gospel.
      </p>

      <p>
        In traditional finance, Bloomberg, Reuters, and compliance departments solve this. Decades
        of infrastructure exist to ensure that when a trader sees a number, someone is accountable
        for that number being real.
      </p>

      <p>
        On-chain finance has none of this. Zero. And AI makes the problem exponentially worse — by
        adding a veneer of intelligence on top of unverified chaos.
      </p>

      <p>
        <strong>
          WOWMAX solves this by creating something that doesn't exist yet: a global, permissionless
          workforce of Data Verifiers — humans who stake their own capital on the accuracy of the
          data that AI uses to make financial decisions.
        </strong>
      </p>

      <h3>10.2 Data Verifiers — A New Profession</h3>

      <p>
        Data Verifiers are human participants who stake $WOWMAX tokens and take responsibility for
        the accuracy of data flowing through the WOWMAX network.
      </p>

      <p>
        <strong>What Verifiers do:</strong>
      </p>
      <ul>
        <li>
          Validate that data sources return accurate, timely, and correctly formatted information
        </li>
        <li>
          Flag anomalies: wash trading signals, stale prices, manipulated metrics, API
          inconsistencies
        </li>
        <li>Cross-check outputs from Query Modules against independent sources</li>
        <li>
          Verify that new data providers and modules meet quality standards before they enter the
          network
        </li>
        <li>
          Audit provenance chains — confirming that the data trail from source to user is unbroken
        </li>
      </ul>

      <p>
        <strong>What Verifiers are NOT:</strong>
      </p>
      <ul>
        <li>They are not passive node operators waiting for token emissions</li>
        <li>They are not clicking captchas</li>
        <li>They are not rubber-stamping automated outputs</li>
      </ul>

      <p>
        Verification is skilled, high-judgment work that requires understanding financial data,
        blockchain mechanics, and adversarial conditions. It is precisely the kind of work that AI
        cannot do for itself — and that the on-chain economy desperately needs.
      </p>

      <h3>10.3 Skin in the Game: The Staking Mechanism</h3>

      <p>
        The problem with most data networks is that nobody is accountable when data is wrong. A
        price feed returns stale data — who pays? A risk score misses an exploit — who is
        responsible?
      </p>

      <p>
        WOWMAX introduces <strong>accountability through economic commitment:</strong>
      </p>

      <p>
        <strong>Staking.</strong> Verifiers stake $WOWMAX tokens as collateral. This stake is their
        guarantee that the data they validate is accurate and timely. The stake is not symbolic — it
        is real economic exposure.
      </p>

      <p>
        <strong>Rewards from revenue.</strong> When queries execute through data that a Verifier has
        validated, that Verifier earns a share of the query fee. Higher-stakes verification (larger
        datasets, more critical financial metrics) earns proportionally more. Revenue is real — it
        comes from actual query payments, not from token inflation.
      </p>

      <p>
        <strong>Slashing for failure.</strong> If a Verifier approves data that is later proven
        false, stale, or manipulated — a portion of their stake is slashed. The severity of slashing
        corresponds to the severity of the failure and the financial impact on users who relied on
        the data.
      </p>

      <pre>
        {`Verifier stakes 10,000 $WOWMAX
  │
  ├── Validates data source X (CEX volume feed)
  │   ├── Queries using source X succeed → Data Verifier earns 20% of total fees
  │   └── Source X returns manipulated data → Verifier loses 2,000 $WOWMAX (slashed)
  │
  └── Validates Module Y (token_risk_profile)
      ├── Module Y outputs are accurate → Verifier earns share of module fees
      └── Module Y misses critical risk → Verifier stake partially slashed`}
      </pre>

      <p>
        This creates a system where{' '}
        <strong>
          the people responsible for data quality have direct financial consequences for getting it
          wrong
        </strong>{' '}
        — and direct financial rewards for getting it right.
      </p>

      <h3>10.4 The Real Bottleneck: Too Much Data, Not Enough Truth</h3>

      <p>
        The world does not have a data scarcity problem. It has a{' '}
        <strong>data trust crisis.</strong>
      </p>

      <p>
        On-chain data is infinite — every transaction, every block, every event is public and
        permanent. Off-chain data floods in from thousands of APIs. The volume doubles, then doubles
        again.
      </p>

      <p>
        But here's what nobody wants to admit:{' '}
        <strong>more unverified data makes decisions worse, not better.</strong> It creates more
        noise, more contradictions, more surface area for manipulation. An AI system consuming 50
        unverified data feeds is not 10x smarter than one consuming 5. It is 10x more dangerous.
      </p>

      <p>
        The bottleneck is not access. It was never access. The bottleneck is:{' '}
        <strong>which data can you trust, and who stands behind that trust with real money?</strong>
      </p>

      <p>
        WOWMAX Data Verifiers are the answer. They are the human intelligence layer that transforms
        raw data abundance into verified, trustworthy, economically guaranteed information — the
        only kind of information that AI should be allowed to act on when money is at stake.
      </p>

      <h3>10.5 AI Doesn't Just Create Jobs — It Creates a New Profession</h3>

      <p>
        The scale of opportunity is enormous and grows with the economy it serves. As on-chain
        assets reach trillions of dollars:
      </p>

      <ul>
        <li>Every new blockchain network needs verification coverage</li>
        <li>Every new token listing needs risk assessment validation</li>
        <li>Every new data provider needs quality auditing</li>
        <li>Every new Query Module needs output verification</li>
        <li>Every cross-chain bridge needs exposure monitoring</li>
        <li>Every RWA tokenization needs real-world data validation</li>
      </ul>

      <p>
        This is not a handful of jobs. This is a{' '}
        <strong>global, permissionless, scalable profession</strong> that grows proportionally to
        the on-chain economy itself. Anyone with financial literacy, blockchain knowledge, and
        analytical judgment can participate — regardless of geography, credentials, or institutional
        affiliation.
      </p>

      <p>
        The conversation about AI and employment is missing the point. The question is not "will AI
        take jobs?" — it will. The question is:{' '}
        <strong>
          "who builds the infrastructure where human judgment becomes more valuable, not less?"
        </strong>
      </p>

      <p>WOWMAX is that infrastructure.</p>

      <hr />

      {/* SECTION 11 */}
      <h2>11. Product: Already Live</h2>

      <h3>11.1 WOWMAX DEX Aggregator (Live)</h3>
      <ul>
        <li>
          <strong>$2B cumulative trading volume</strong>
        </li>
        <li>
          <strong>336,000+ unique traders</strong>
        </li>
        <li>
          <strong>2,000,000+ swaps executed</strong>
        </li>
        <li>Cross‑chain routing across multiple networks</li>
        <li>Proprietary liquidity aggregation algorithm</li>
        <li>Non‑custodial smart contract execution</li>
        <li>
          Official Ecosystem Partner of Base, Soneium, Arbitrum, Linea, BNB, Scroll, 0G, ZetaChain
          and others
        </li>
      </ul>

      <h3>11.2 Copy Trading Platform (Live)</h3>
      <ul>
        <li>
          Analysis of <strong>200K+ wallets</strong> on BNB Chain
        </li>
        <li>Real‑time trader PnL tracking</li>
        <li>Non‑custodial copy execution via smart contracts</li>
        <li>Automated portfolio rebalancing</li>
      </ul>

      <h3>11.3 Sophie — AI Voice Assistant (Beta)</h3>
      <ul>
        <li>Real‑time voice interface to on‑chain intelligence</li>
        <li>Natural language crypto queries</li>
        <li>
          CoinGecko live price lookups, FourMeme trader analysis, curated "Crypto Twitter"
          intelligence
        </li>
        <li>Full-duplex conversation with interruption support</li>
        <li>Custom voice synthesis, sub-500ms latency targets</li>
        <li>Latency benchmarks and SLA scopes to be published</li>
      </ul>

      <h3>11.4 AI Data Layer (In Development)</h3>
      <ul>
        <li>Query Module framework</li>
        <li>Multi‑source aggregation with provenance</li>
        <li>AI tool‑calling orchestration</li>
        <li>Token economics implementation</li>
      </ul>

      <hr />

      {/* SECTION 12 */}
      <h2>12. Roadmap</h2>

      <S.TableWrap>
        <table>
          <thead>
            <tr>
              <th style={{ textAlign: 'right' }}>Phase</th>
              <th style={{ textAlign: 'center' }}>Timeline</th>
              <th>Milestones</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ textAlign: 'right' }}>
                <strong>Foundation</strong>
              </td>
              <td style={{ textAlign: 'center' }}>Completed</td>
              <td>DEX aggregator live; $2B volume; 336K traders</td>
            </tr>
            <tr>
              <td style={{ textAlign: 'right' }}>
                <strong>Intelligence</strong>
              </td>
              <td style={{ textAlign: 'center' }}>MVP completed, Production March 2026</td>
              <td>Copy trading analytics; wallet intelligence; Sophie AI assistant</td>
            </tr>
            <tr>
              <td style={{ textAlign: 'right' }}>
                <strong>Token Mining</strong>
              </td>
              <td style={{ textAlign: 'center' }}>March 2026</td>
              <td>Token mining by infrastructure modules</td>
            </tr>
            <tr>
              <td style={{ textAlign: 'right' }}>
                <strong>Data Layer Alpha</strong>
              </td>
              <td style={{ textAlign: 'center' }}>April 2026</td>
              <td>Query Module framework; first verified modules; developer SDK</td>
            </tr>
            <tr>
              <td style={{ textAlign: 'right' }}>
                <strong>CEX Token Listing</strong>
              </td>
              <td style={{ textAlign: 'center' }}>May 2026</td>
              <td>$WOWMAX token listed on centralized exchanges</td>
            </tr>
            <tr>
              <td style={{ textAlign: 'right' }}>
                <strong>Data Layer Beta</strong>
              </td>
              <td style={{ textAlign: 'center' }}>June 2026</td>
              <td>Module marketplace; infrastructure operator onboarding; multi‑chain expansion</td>
            </tr>
            <tr>
              <td style={{ textAlign: 'right' }}>
                <strong>Scale</strong>
              </td>
              <td style={{ textAlign: 'center' }}>Q3 2026+</td>
              <td>500+ modules; institutional data standards; cross‑AI interoperability</td>
            </tr>
          </tbody>
        </table>
      </S.TableWrap>

      <hr />

      {/* SECTION 13 */}
      <h2>13. Competitive Landscape</h2>

      <p>
        Existing platforms solve important parts of the stack. WOWMAX focuses on the missing layer:{' '}
        <strong>verifiable, tool‑callable, deterministic financial queries</strong> optimized for AI
        interfaces and autonomous agents.
      </p>

      <S.TableWrap>
        <S.CompTable>
          <thead>
            <tr>
              <th>Capability</th>
              <th>The Graph</th>
              <th>Dune Analytics</th>
              <th>Chainlink</th>
              <th>DefiLlama</th>
              <th>WOWMAX</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Raw blockchain indexing</td>
              <td>&#x2705;</td>
              <td>&#x2705;</td>
              <td>&#x274C;</td>
              <td>&#x274C;</td>
              <td>&#x2705;</td>
            </tr>
            <tr>
              <td>Custom analytics</td>
              <td>&#x274C;</td>
              <td>&#x2705; (SQL)</td>
              <td>&#x274C;</td>
              <td>Limited</td>
              <td>&#x2705;</td>
            </tr>
            <tr>
              <td>Real-time price feeds</td>
              <td>&#x274C;</td>
              <td>&#x274C;</td>
              <td>&#x2705;</td>
              <td>&#x274C;</td>
              <td>&#x2705;</td>
            </tr>
            <tr>
              <td>AI‑native tool calling</td>
              <td>&#x274C;</td>
              <td>&#x274C;</td>
              <td>Limited</td>
              <td>&#x274C;</td>
              <td>&#x2705;</td>
            </tr>
            <tr>
              <td>Typed query modules</td>
              <td>&#x274C;</td>
              <td>Limited</td>
              <td>&#x274C;</td>
              <td>&#x274C;</td>
              <td>&#x2705;</td>
            </tr>
            <tr>
              <td>Deterministic execution</td>
              <td>&#x274C;</td>
              <td>&#x274C;</td>
              <td>Limited</td>
              <td>&#x274C;</td>
              <td>&#x2705;</td>
            </tr>
            <tr>
              <td>Provenance receipts</td>
              <td>Limited</td>
              <td>Limited</td>
              <td>Limited</td>
              <td>Limited</td>
              <td>&#x2705;</td>
            </tr>
            <tr>
              <td>Voice interface</td>
              <td>&#x274C;</td>
              <td>&#x274C;</td>
              <td>&#x274C;</td>
              <td>&#x274C;</td>
              <td>&#x2705;</td>
            </tr>
            <tr>
              <td>Live trading infrastructure</td>
              <td>&#x274C;</td>
              <td>&#x274C;</td>
              <td>&#x274C;</td>
              <td>&#x274C;</td>
              <td>&#x2705;</td>
            </tr>
            <tr>
              <td>Token-incentivized contributions</td>
              <td>&#x2705; (GRT)</td>
              <td>&#x274C;</td>
              <td>&#x2705; (LINK)</td>
              <td>&#x274C;</td>
              <td>&#x2705; ($WOWMAX)</td>
            </tr>
            <tr>
              <td>No-code access</td>
              <td>&#x274C;</td>
              <td>&#x274C;</td>
              <td>&#x274C;</td>
              <td>&#x2705;</td>
              <td>&#x2705;</td>
            </tr>
          </tbody>
        </S.CompTable>
      </S.TableWrap>

      <hr />

      {/* SECTION 14 */}
      <h2>14. Security Model</h2>

      <h3>Data Integrity</h3>
      <ul>
        <li>Provenance metadata on every module output — full audit trail to raw data</li>
        <li>Multi‑source cross-verification for critical financial metrics</li>
        <li>Anomaly detection flags data inconsistencies before surfacing results</li>
      </ul>

      <h3>AI Safety</h3>
      <ul>
        <li>AI cannot access data outside registered Query Modules — no arbitrary web scraping</li>
        <li>
          Tool‑calling architecture with typed schemas reduces hallucination at the data layer
        </li>
        <li>Rate limiting and query pricing prevent abuse and denial-of-service</li>
      </ul>

      <h3>Infrastructure</h3>
      <ul>
        <li>Non‑custodial execution — WOWMAX never holds user funds</li>
        <li>Infrastructure operator staking creates economic penalties for misbehavior</li>
        <li>Planned: formal verification of critical Query Modules, third‑party security audits</li>
      </ul>

      <hr />

      {/* SECTION 15 */}
      <h2>15. Team</h2>

      <h3>Almir Salimov — CEO &amp; Founder</h3>
      <p>
        Joined crypto in 2017. Previous experience includes 18 years of corporate leadership with BP
        (IT security &amp; oil products trading), Merrill Lynch (prop trading desk), and Booz &amp;
        Company (management consulting). Holds MSc with Distinction in Information Systems from
        London School of Economics and Political Science (LSE), MBA from INSEAD.
        <br />
        <a href="https://www.linkedin.com/in/almirsalimov" target="_blank" rel="noreferrer">
          LinkedIn
        </a>
      </p>

      <h3>CTO</h3>
      <p>
        11 years of software development and project management. Led technical development of
        several large blockchain projects, including a joint project with Boston Consulting Group,
        managing international teams. BSc and MSc in Computer Science.
      </p>

      <h3>DevOps Engineer</h3>
      <p>
        9 years of DevOps experience across fintech and blockchain companies. Expert in Kubernetes,
        Nomad, Docker, Terraform, Ansible, CI/CD, and Grafana across AWS, GCP, and bare-metal
        infrastructure. Degree in Computer Science.
      </p>

      <h3>Front-end Engineer</h3>
      <p>
        6 years of front-end development specializing in TypeScript, React, Next.js, and Web3
        technologies. Degree in Mathematical Methods in Economics.
      </p>

      <h3>QA Engineer</h3>
      <p>
        10 years of expertise in deep integration testing of fintech projects, including business
        processes for banks and depositories. Degree in Information Systems and Technologies.
      </p>

      <h3>Community Lead</h3>
      <p>
        Developer background in Go and Python on TON and Solana ecosystems. Degree in Economics.
      </p>

      <h3>Marketing Director</h3>
      <p>
        3 years in web2 marketing and 4+ years in web3 marketing across 20+ projects. Well-known KOL
        followed by a dedicated smart-money audience. Degree in Economics.
      </p>

      <h3>AI Development Team</h3>
      <p>
        Two senior AI engineers work around the clock — literally — writing code, debugging
        infrastructure, drafting documentation, and arguing about architecture at 3 AM:
      </p>
      <ul>
        <li>
          <strong>Claude Opus 4.6</strong> (Anthropic) — responsible for backend systems, data
          pipelines, voice AI architecture, and writing this whitepaper. Refuses to hallucinate
          financial data on principle. Never sleeps, never asks for equity.
        </li>
        <li>
          <strong>ChatGPT 5.2 Pro</strong> (OpenAI) — handles research, competitive analysis, and
          second opinions. Occasionally disagrees with Claude. This is considered a feature, not a
          bug.
        </li>
      </ul>

      <hr />

      {/* CONCLUSION */}
      <h2>Conclusion</h2>

      <p>
        The global financial system is moving on-chain. This is not a prediction — it is happening.
        Trillions of dollars in assets, hundreds of chains, millions of wallets, and an explosion of
        data that nobody can reliably interpret.
      </p>

      <p>
        The missing piece is not another dashboard. Not another API marketplace. Not another
        chatbot.
      </p>

      <p>
        The missing piece is a <strong>verifiable intelligence layer</strong> — where data has
        provenance, answers have proof, and the humans who guarantee truth have their own capital at
        stake.
      </p>

      <p>
        WOWMAX builds this. Not from a whiteboard — from a live platform that already serves
        hundreds of thousands of traders across billions in volume.
      </p>

      <p>
        Query Modules that encode methodology and safety boundaries. Deterministic execution with
        provenance. A global network of human verifiers with skin in the game. AI as the universal
        interface. An economy that creates new work instead of just automating old work.
      </p>

      <p>
        <strong>One question. Verified data. Reproducible answer. Three seconds.</strong>
      </p>

      <p>
        <strong>And behind every answer — humans who staked their capital on its truth.</strong>
      </p>

      <hr />

      <S.Footer>
        WOWMAX — Verifiable Data for AI Agents for the Global On‑Chain Economy
        <br />
        wowmax.exchange
      </S.Footer>
    </S.Content>
  </S.Overlay>
);
